import { PropertyFilters } from '@/types/property';

/**
 * Serializes PropertyFilters to URL search parameters
 * Handles arrays, numbers, and strings correctly
 */
export function serializeFilters(filters: PropertyFilters): URLSearchParams {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    // Skip undefined, null, empty strings
    if (value === undefined || value === null || value === '') {
      return;
    }

    // Handle arrays (e.g., features: ['pool', 'garage'])
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params.set(key, value.join(','));
      }
      return;
    }

    // Handle all other types (strings, numbers, booleans)
    params.set(key, String(value));
  });

  return params;
}

/**
 * Deserializes URL search parameters to PropertyFilters
 * Reconstructs arrays and converts number strings back to numbers
 */
export function deserializeFilters(searchParams: URLSearchParams): PropertyFilters {
  const filters: PropertyFilters = {};

  // Helper to check if a value should be parsed as a number
  const isNumericField = (key: string): boolean => {
    return ['minPrice', 'maxPrice', 'bedrooms', 'bathrooms', 'minArea', 'maxArea'].includes(key);
  };

  // Helper to check if a value should be parsed as an array
  const isArrayField = (key: string): boolean => {
    return ['features'].includes(key);
  };

  searchParams.forEach((value, key) => {
    // Skip empty values
    if (!value || value.trim() === '') {
      return;
    }

    // Parse arrays (comma-separated values)
    if (isArrayField(key)) {
      (filters as any)[key] = value.split(',').map(v => v.trim()).filter(v => v !== '');
      return;
    }

    // Parse numbers
    if (isNumericField(key)) {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        (filters as any)[key] = numValue;
      }
      return;
    }

    // Parse strings (including operation and type)
    (filters as any)[key] = value;
  });

  return filters;
}

/**
 * Merges new filters with existing filters and returns updated URLSearchParams
 * Useful for updating only specific filters without losing others
 */
export function updateFiltersInUrl(
  currentParams: URLSearchParams,
  newFilters: Partial<PropertyFilters>
): URLSearchParams {
  const currentFilters = deserializeFilters(currentParams);
  const mergedFilters = { ...currentFilters, ...newFilters };

  // Remove undefined/null values from merged filters
  Object.keys(mergedFilters).forEach((key) => {
    const value = (mergedFilters as any)[key];
    if (value === undefined || value === null || value === '') {
      delete (mergedFilters as any)[key];
    }
  });

  return serializeFilters(mergedFilters);
}

/**
 * Clears specific filters from URL
 */
export function clearFiltersFromUrl(
  currentParams: URLSearchParams,
  filterKeys: (keyof PropertyFilters)[]
): URLSearchParams {
  const currentFilters = deserializeFilters(currentParams);

  filterKeys.forEach(key => {
    delete currentFilters[key];
  });

  return serializeFilters(currentFilters);
}

/**
 * Gets a human-readable description of active filters
 * Useful for displaying "Active filters: 3" or similar UI
 */
export function getActiveFiltersCount(filters: PropertyFilters): number {
  return Object.keys(filters).filter(key => {
    const value = (filters as any)[key];
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    // Don't count 'operation' as an active filter (it's usually default)
    if (key === 'operation') return false;
    return true;
  }).length;
}

/**
 * Validates that filters conform to expected types
 * Returns cleaned filters with invalid values removed
 */
export function validateFilters(filters: PropertyFilters): PropertyFilters {
  const validFilters: PropertyFilters = {};

  // Validate operation
  if (filters.operation && ['VENTA', 'ARRIENDO', 'ARRIENDO_TEMPORAL'].includes(filters.operation)) {
    validFilters.operation = filters.operation;
  }

  // Validate type
  if (filters.type && ['casa', 'departamento', 'oficina', 'terreno', 'bodega'].includes(filters.type)) {
    validFilters.type = filters.type;
  }

  // Validate numeric fields
  if (typeof filters.minPrice === 'number' && filters.minPrice >= 0) {
    validFilters.minPrice = filters.minPrice;
  }
  if (typeof filters.maxPrice === 'number' && filters.maxPrice >= 0) {
    validFilters.maxPrice = filters.maxPrice;
  }
  if (typeof filters.bedrooms === 'number' && filters.bedrooms >= 0) {
    validFilters.bedrooms = filters.bedrooms;
  }
  if (typeof filters.bathrooms === 'number' && filters.bathrooms >= 0) {
    validFilters.bathrooms = filters.bathrooms;
  }
  if (typeof filters.minArea === 'number' && filters.minArea >= 0) {
    validFilters.minArea = filters.minArea;
  }
  if (typeof filters.maxArea === 'number' && filters.maxArea >= 0) {
    validFilters.maxArea = filters.maxArea;
  }

  // Validate string fields
  if (typeof filters.location === 'string' && filters.location.trim() !== '') {
    validFilters.location = filters.location.trim();
  }
  if (typeof filters.search === 'string' && filters.search.trim() !== '') {
    validFilters.search = filters.search.trim();
  }

  // Validate array fields
  if (Array.isArray(filters.features) && filters.features.length > 0) {
    validFilters.features = filters.features.filter(f => typeof f === 'string' && f.trim() !== '');
  }

  return validFilters;
}
